import { SiteSection } from '../SiteSection.js';

/**
 * You keep your number (§22).
 *
 * The section that removes the objection everybody has, so it is drawn as a
 * path rather than argued in a paragraph: the call arrives on the line the
 * salon already has, and only what would have rung in the void reaches us.
 *
 * Marked up as content, not as an image: it reflows on a phone, the text can
 * be selected and found, and a screen reader gets the steps in order. The
 * dots mean what they mean in the diary at the top of the page — amber, a
 * call nobody can take; teal, the call GSI Voice takes — and the words say it
 * too.
 */
export function Overflow(): React.JSX.Element {
  return (
    <SiteSection
      title="Vous gardez votre numéro."
      intro={
        <p>
          Rien à porter, rien à réimprimer. GSI Voice prend le relais quand votre équipe ne peut pas
          répondre, et vous l’arrêtez en retirant le renvoi.
        </p>
      }
    >
      <div className="max-w-2xl">
        <Step title="Votre numéro actuel">
          Celui de vos cartes, de votre vitrine et de votre fiche Google.
        </Step>
        <Joint />
        <Step title="Le salon sonne">Vous décrochez si vous le pouvez. Rien ne change.</Step>
        <Joint />

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-[10px] border border-wall-line bg-surface p-5">
            <p className="text-ui-sm text-ink-muted">Vous décrochez</p>
            <p className="mt-1 text-subtitle font-medium text-ink">C’est vous</p>
            <p className="mt-2 text-ui-sm text-ink-muted">
              L’assistante ne s’en mêle pas. Elle ne vous coupe jamais la parole.
            </p>
          </div>

          <div className="rounded-[10px] border border-teal-ink bg-surface p-5">
            <p className="flex items-center gap-2 text-ui-sm text-ink-muted">
              <span aria-hidden="true" className="h-2 w-2 rounded-pill bg-ringing" />
              Personne ne décroche
            </p>
            <p className="mt-1 flex items-center gap-2 text-subtitle font-medium text-teal-ink">
              <span aria-hidden="true" className="h-2 w-2 rounded-pill bg-teal-ink" />
              GSI Voice répond
            </p>
            <p className="mt-2 text-ui-sm text-ink-muted">
              L’appel est pris, le rendez-vous est posé, vous le retrouvez dans votre agenda.
            </p>
          </div>
        </div>
      </div>
    </SiteSection>
  );
}

function Step({
  title,
  children,
}: {
  readonly title: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="rounded-[10px] border border-wall-line bg-surface p-5">
      <p className="text-subtitle font-medium text-ink">{title}</p>
      <p className="mt-1 text-ui-sm text-ink-muted">{children}</p>
    </div>
  );
}

/** The line from one step to the next. */
function Joint(): React.JSX.Element {
  return <div aria-hidden="true" className="ml-8 h-6 w-px bg-line-control" />;
}
